import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Mail, Phone, Paperclip, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, isPdf } from "../../lib/api";
import { PageTitle, Card } from "./AdminUI";

const TABS = [["", "Tutte"], ["contact", "Contatti"], ["property", "Proposte immobili"], ["investor", "Investitori"], ["operation", "Operazioni"], ["book_preview", "Anteprima libro"], ["book_order", "Ordini libro"]];
const TYPE = { contact: "Contatto", property: "Proposta immobile", investor: "Investitore", operation: "Dossier operazione", book_preview: "Anteprima libro", book_order: "Ordine libro" };

const LeadRow = ({ l, onRead, onDelete }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => { setOpen(!open); if (!l.read) onRead(l); };
  return (
    <div data-testid={`admin-lead-${l.id}`} className={`border-b border-gray-100 ${!l.read ? "bg-gold/5" : ""}`}>
      <button type="button" onClick={toggle} className="w-full text-left p-4 flex items-center gap-4">
        <span className={`w-2 h-2 rounded-full shrink-0 ${l.read ? "bg-transparent" : "bg-gold"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3"><span className="font-medium text-sm">{l.name}</span><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-gray-100 text-stone">{TYPE[l.type]}</span>{l.city && <span className="text-xs text-stone">{l.city}</span>}</div>
          <div className="text-xs text-stone truncate mt-0.5">{l.details?.operazione ? `${l.details.operazione} · ` : ""}{l.message || l.details?.indirizzo || "—"}</div>
        </div>
        <span className="text-xs text-stone whitespace-nowrap">{new Date(l.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
        <ChevronDown size={16} className={`text-stone transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-5 pl-10 text-sm space-y-4">
          <div className="flex flex-wrap gap-5 text-ink/80">
            <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-gold"><Mail size={14} />{l.email}</a>
            {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 hover:text-gold"><Phone size={14} />{l.phone}</a>}
          </div>
          {Object.keys(l.details || {}).length > 0 && (
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 bg-gray-50 p-4 rounded-md">
              {Object.entries(l.details).filter(([, v]) => v !== "" && v !== null && v !== false).map(([k, v]) => <div key={k}><dt className="text-[10px] uppercase tracking-wider text-stone">{k.replace(/_/g, " ")}</dt><dd className="text-sm">{String(v === true ? "sì" : v)}</dd></div>)}
            </dl>
          )}
          {l.message && <p className="whitespace-pre-wrap text-ink/80">{l.message}</p>}
          {l.marketing_consent && <p className="text-xs text-sage">✓ Consenso marketing prestato</p>}
          {l.attachments?.length > 0 && (
            <div><div className="text-[10px] uppercase tracking-wider text-stone mb-2 inline-flex items-center gap-1"><Paperclip size={12} /> Allegati</div>
              <div className="flex flex-wrap gap-2">{l.attachments.map((a, i) => <a key={i} href={imgUrl(a)} target="_blank" rel="noreferrer" className="w-24 h-24 border rounded overflow-hidden bg-gray-50 flex items-center justify-center text-xs text-stone">{isPdf(a) ? "PDF" : <img src={imgUrl(a)} alt="" className="img-cover" />}</a>)}</div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => onRead(l, !l.read)} className="text-xs text-stone hover:text-ink">{l.read ? "Segna come da leggere" : "Segna come letta"}</button>
            <button type="button" onClick={() => onDelete(l)} data-testid={`admin-lead-delete-${l.id}`} className="text-xs text-red-600 inline-flex items-center gap-1"><Trash2 size={12} /> Elimina</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function LeadsAdmin() {
  const [tab, setTab] = useState("");
  const qc = useQueryClient();
  const { data: leads = [] } = useQuery({ queryKey: ["admin-leads", tab], queryFn: () => api.get(`/admin/leads${tab ? `?type=${tab}` : ""}`).then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-leads"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };
  const onRead = async (l, read = true) => { await api.patch(`/admin/leads/${l.id}`, { read }); refresh(); };
  const onDelete = async (l) => { if (!window.confirm("Eliminare questa richiesta?")) return; await api.delete(`/admin/leads/${l.id}`); toast.success("Richiesta eliminata"); refresh(); };
  return (
    <div data-testid="admin-leads">
      <PageTitle title="Richieste" subtitle="Form contatti, proposte immobiliari e richieste investitori" />
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {TABS.map(([v, l]) => <button key={v} onClick={() => setTab(v)} data-testid={`admin-leads-tab-${v || "all"}`} className={`px-4 py-2 text-xs uppercase tracking-wider rounded-md border transition-colors ${tab === v ? "bg-ink text-white border-ink" : "bg-white border-gray-200 text-stone hover:border-ink"}`}>{l}</button>)}
      </div>
      <Card className="!p-0 overflow-hidden">
        {leads.map((l) => <LeadRow key={l.id} l={l} onRead={onRead} onDelete={onDelete} />)}
        {leads.length === 0 && <p className="p-6 text-sm text-stone">Nessuna richiesta.</p>}
      </Card>
    </div>
  );
}
